<template>
  <div id="AccountDomains" class="Main-Content">
    <LoadingLayout v-if="loading" :page="true" />
    <Page
      v-else
      :title="$t('Domains')"
      :primaryAction="{
        label: $t('Add Domain'),
        disabled: saving,
        onAction: () => addDomain()
      }"
      :secondaryActions="[{
        label: $t('Notes'),
        onAction: () => eventHub.$emit('showAccountNotes', 'DOMAIN')
      }]"
    >
      <Layout>
        <LayoutSection>
          <ValidationObserver v-slot="{ invalid }" slim>
            <Sheet
              :title="$t('Options')"
              :width="700"
              :visible="!!editSite"
              @close="closeEdit"
              padded
            >
              <template v-slot:actions>
                <Button type="primary" :disabled="invalid || saving" @click="updateDomain">{{$t('Update')}}</Button>
              </template>
              <Card v-if="editSite">
                <CardSection>
                  <FormLayout>
                    <!-- <ValidationProvider :name="$t('Default Currency')" immediate rules="required" v-slot="{ errors }"> -->
                      <Select 
                        :label="$t('Default Currency')"
                        :placeholder="$t('Choose Default Currency')"
                        v-model="editSiteAccess.defaultCurrency"
                        :options="currencies"
                      />
                    <!-- </ValidationProvider> -->
                    <Checkbox
                      :label="$t('Override Session')"
                      v-model="editSiteAccess.defaultCurrencyOverride"
                    />
                    <Select
                      :label="$t('Default Language')"
                      :placeholder="$t('Choose Default Language')"
                      v-model="editSiteAccess.defaultLanguage"
                      :options="languages"
                    />
                    <ValidationProvider name="regex" rules="langRegex" v-slot="{ errors }">
                      <TextField
                        v-model="editSiteAccess.defaultLanguageRegex"
                        :placeholder="$t('Regex to identify language from the page URL')"
                        :error="errors[0]"
                      />
                    </ValidationProvider>
                  </FormLayout>
                </CardSection>
              </Card>
            </Sheet>
          </ValidationObserver>
          <Card>
            <CardSection subdued>
              <div class="intro-text" v-html="$t('AccountDomains.infoText')"></div>
            </CardSection>
            
            <Filters
              :query.sync="filter"
            />
            <DataTable
              :columns="[
                {
                  key: 'domain',
                  label: $t('Domain'),
                  fixed: true
                },
                {
                  key: 'campaignLink',
                  label: $t('Campaign')
                },
                {
                  key: 'fullLanguage',
                  label: $t('Language')
                },
                {
                  key: 'fullCurrency',
                  label: $t('Currency')
                },
                {
                  key: 'regexExists',
                  label: $t('Language Regex Exists')
                }
              ]"
              :row-actions="(site) => (getRowActions(site))"
              :rows="filteredSites"
            >
              <template v-slot:col:domain="{row}">
                <PageLink @click="editSiteOptions(row)">{{row.domain}}</PageLink>
              </template>
              <template v-slot:col:campaignLink="{row}">
                <template v-if="row.campaignId">{{row.campaignLink}}</template>
                <Badge v-else type="warning">{{$t('AccountDomains.Not Linked')}}</Badge>
              </template>
            </DataTable>
          </Card>
        </LayoutSection>
      </Layout>
    </Page>
  </div>
</template>

<script>

import _ from 'lodash'
import isValidDomain from 'is-valid-domain'
import { extend as ValidateExtend } from 'vee-validate/dist/vee-validate.full'

export default {
  name: 'AccountDomains',
  data () {
    return {
      loading: true,
      saving: false,
      sites: [],
      currencies: [],
      languages: [],
      filter: '',
      editSite: null,
      editSiteCopy: null,
      campaigns: null
    }
  },
  mounted() {
    ValidateExtend('langRegex', (value) => {
      if (value && !value.match(/^\/.*\(.+\).*\/[igm]*$/)) {
        return this.$t('AccountDomains.langRegexValidate')
      }
      return true
    })
    NProgress.start()
    Promise.all([this.api.getCampaignNames(), this.api.getDomains(), this.api.getCurrenciesList(), this.api.getLanguagesList()])
      .then((response) => {
        let [ campaigns, sites, currencies, languages ] = response
        this.campaigns = campaigns
        this.sites = sites
        this.currencies = currencies
        this.languages = languages
        // console.log(response)
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    editSiteAccess() {
      return this.editSite || {}
    },
    filteredSites() {
      const filter = this.filter.trim();
      let sites = this.sites
      if (filter.length) {
        const self = this;
        sites = sites.filter((o) => {
          return (
            o.id === filter ||
            o.domain.match(new RegExp(filter)) ||
            self.getCampaignName(o.campaignId).match(new RegExp(filter))
          );
        });
      }
      // console.log('filteredSites', sites)
      // return sites
      return sites.map(s => {
        const l = this.languages.find(l => l.value === s.defaultLanguage)
        const c = this.currencies.find(c => c.value === s.defaultCurrency)
        let link = ''
        if (s.campaignId) {
          const campName = this.getCampaignName(s.campaignId)
          if (campName) {
            link = `${this.$t('Linked to:')} '${campName}'`
          } else {
            link = `${this.$t('Linked to:')} '(???)'`
          }
        }
        return {
          ...s,
          campaignLink: link,
          fullLanguage: l ? l.value : null,
          fullCurrency: c ? c.value : null,
          regexExists: s.defaultLanguageRegex ? this.$t('Yes') : this.$t('No')
        }
      })
    }
  },

  methods: {
    getRowActions(site) {
      const rowActions = []
      if (site.campaignId) {
        rowActions.unshift({
          label: this.$t('Go To Campaign'),
          onAction: () => {
            const routeData = this.$router.resolve('/campaigns/' + site.campaignId +'/domains')
            window.open(routeData.href, '_blank');
          }
        })
      } else {
        rowActions.push({
          label: this.$t('Delete'),
          onAction: () => (this.removeDomain(site))
        })
      }
      // rowActions.push({
      //   label: this.$t('Edit'),
      //   onAction: () => (this.editSiteOptions(site))
      // })
      return rowActions
    },  
    editSiteOptions(site) {
      let matchedSite = this.sites.find(s => s.id === site.id)
      this.editSite = _.cloneDeep(matchedSite)
      this.editSiteCopy = _.cloneDeep(matchedSite)
    },
    async addDomain() {
      const newDomain = await this.$ui.prompt({
        title: this.$t('AccountDomains.Add New Domain'),
        content: this.$t('AccountDomains.Enter a new domain'),
        primaryActionLabel: this.$t('Add'),
        inputPlaceholder: this.$t('AccountDomains.Domain name'),
        inputValidator: (val) => {
          const valid = val && isValidDomain(val.trim(), { subdomain: false, wildcard: false })
          if (!valid) {
            return this.$t('Invalid domain')
          }
          let alreadyExists = false
          for (const site of this.sites) {
            if (site.domain === val) {
              alreadyExists = true
              break
            }
          }
          if (alreadyExists) {
            return this.$t('Domain already exists')
          }
          return true
        }
      })
      if (!newDomain || !newDomain.trim()) {
        return
      }
      NProgress.start()
      this.saving = true
      this.api.addAccountSite(newDomain.trim())
        .then((newDomainId) => {
          this.sites.push({
            domain: newDomain,
            id: newDomainId,
            campaignId: null
          })
          this.$ui.toast({ title: this.$t('Saved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    },  
    getCampaignName(campId) {
      for (const camp of this.campaigns) {
        if (camp.id === campId) {
          return camp.name
        }
      }
      return '';
    },
    async removeDomain(site) {
      const shouldRemove = await this.$ui.confirm({ 
        title: this.$t('Remove Domain'), 
        content: this.$t('AccountDomains.Are you sure you want to remove domain', {value: site.domain})
      })
      if (shouldRemove) {
        NProgress.start()
        this.saving = true
        this.api.removeAccountSite(site.id)
          .then(() => {
            this.sites.splice(this.sites.findIndex(o => o.id === site.id), 1)
            this.$ui.toast({ title: this.$t('Saved') })
          })
          .catch(this.apiCatch)
          .finally(()=> {
            this.saving = false
            NProgress.done()
          })
      }
    },
    updateDomain() {
      this.saving = true
      this.api.updateAccountSite(this.editSite)
        .then(() => {
          let siteInd = this.sites.findIndex(s => s.id === this.editSite.id)
          this.$set(this.sites, siteInd, this.editSite)
          this.editSite = null
          this.editSiteCopy = null
          this.$ui.toast({ title: this.$t('AccountDomains.Domain Updated') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
        })
    },
    async closeEdit() {
      // console.log('this.editSite', this.editSite)
      // console.log('this.editSiteCopy', this.editSiteCopy)
      // HACK
      if (this.editSite && this.editSiteCopy && this.editSite.defaultLanguageRegex === '' && this.editSiteCopy.defaultLanguageRegex === null) { 
        this.editSite.defaultLanguageRegex = null
      }
      if (!_.isEqual(this.editSite, this.editSiteCopy)) {
        const confirm = await this.$ui.confirm({
          title: this.$t('Unsaved Changes'),
          content: this.$t('AccountDomains.unsavedChangesMessage'),
          primaryActionLabel: this.$t('Lose changes and return to list'),
          primaryActionType: 'destructive',
          secondaryActionLabel: this.$t('Continue editing')
        })
        if (!confirm) {
          return
        }
      }
      this.editSite = null
      this.editSiteCopy = null
    }
  }
}

</script>

<style lang="scss">
#AccountDomains {
  .intro-text {
    div:first-child {
      margin-bottom: 15px;
    }
  }
}
</style>